import * as path from "node:path";
import { TSDocParser, DocComment } from "@microsoft/tsdoc";
import * as ts from "typescript";

/**
 * One row in the TSDoc-coverage report — describes a single public
 * symbol exported (transitively) from the package's entry point and
 * the TSDoc presence flags computed for it.
 */
export interface TsDocCoverageRecord {
  /**
   * The public name of the symbol as it is exported from the package
   * entry point (post-rename for `export { X as Y }` re-exports).
   */
  readonly symbol: string;

  /**
   * Compiler kind label for the declaration backing the symbol — one
   * of `Class`, `Interface`, `TypeAlias`, `Enum`, `Function`,
   * `Variable`, `Module`, `Default`, or `Other`. Useful for grouping
   * findings in the audit report.
   */
  readonly kind: TsDocCoverageKind;

  /**
   * Source location of the underlying declaration (1-indexed line
   * number, file path absolute on disk).
   */
  readonly location: { readonly file: string; readonly line: number };

  /**
   * `true` when the declaration carries a TSDoc block whose
   * `summarySection` is non-empty (i.e. there is at least one
   * paragraph of summary prose).
   */
  readonly hasSummary: boolean;

  /**
   * `true` when the declaration carries a summary that exists but is
   * shorter than the configured `thinSummaryWordThreshold` — useful
   * for advisory-level findings that do not block.
   */
  readonly hasThinSummary: boolean;

  /**
   * `true` when the declaration carries at least one `@param` block
   * tag. Always `false` for kinds that have no parameters
   * (`Variable`, `TypeAlias`, etc.).
   */
  readonly hasParams: boolean;

  /**
   * `true` when the declaration carries a `@returns` block tag.
   * Always `false` for kinds without a return value.
   */
  readonly hasReturns: boolean;
}

/**
 * Compiler-kind label attached to each coverage record.
 */
export const TsDocCoverageKind = {
  Class: "Class",
  Interface: "Interface",
  TypeAlias: "TypeAlias",
  Enum: "Enum",
  Function: "Function",
  Variable: "Variable",
  Module: "Module",
  Default: "Default",
  Other: "Other",
} as const;

/**
 * String-literal type matching the values of {@link TsDocCoverageKind}.
 */
export type TsDocCoverageKind =
  (typeof TsDocCoverageKind)[keyof typeof TsDocCoverageKind];

/**
 * Options accepted by `analyzeTsDocCoverage`.
 */
export interface AnalyzeTsDocCoverageOptions {
  /**
   * Absolute path to the package root (the directory that contains
   * `tsconfig.json` and `src/`).
   */
  readonly packageRoot: string;

  /**
   * Entry point relative to `packageRoot`. Defaults to
   * `src/index.ts` — the convention used by every package in the
   * monorepo.
   *
   * @default "src/index.ts"
   */
  readonly entryPoint?: string;

  /**
   * Word threshold below which a TSDoc summary is flagged as
   * "thin" (advisory only — does not zero-out `hasSummary`). A
   * summary with exactly this many words is considered thin.
   *
   * @default 4
   */
  readonly thinSummaryWordThreshold?: number;

  /**
   * Optional explicit path to a `tsconfig.json`. When omitted, the
   * utility runs with a built-in compiler-options set tailored for
   * AST analysis (no emit, ESNext target, NodeNext module
   * resolution). The built-in options are sufficient for every
   * coverage scan in the docs-sync pipeline; consumers only set
   * this when their package uses an unusual module-resolution
   * strategy that affects symbol resolution.
   */
  readonly tsconfigPath?: string;
}

const DEFAULT_THIN_SUMMARY_WORD_THRESHOLD = 4;
const DEFAULT_ENTRY_POINT = "src/index.ts";

/**
 * Walks a package's public exports, transitively resolving
 * `export *` and `export { X } from "..."` re-exports through the
 * TypeScript compiler API, and reports TSDoc presence flags for
 * every public symbol.
 *
 * Internal symbols (anything not transitively re-exported from
 * `entryPoint`) never appear in the result — the report describes
 * the package's *public surface area*, not its internal modules.
 *
 * @param options - Either an options object or, as a shorthand, the
 *                  absolute path to the package root.
 */
export function analyzeTsDocCoverage(
  options: AnalyzeTsDocCoverageOptions | string,
): Array<TsDocCoverageRecord> {
  const resolvedOptions: AnalyzeTsDocCoverageOptions =
    typeof options === "string" ? { packageRoot: options } : options;

  const packageRoot = path.resolve(resolvedOptions.packageRoot);
  const entryPoint = path.resolve(
    packageRoot,
    resolvedOptions.entryPoint ?? DEFAULT_ENTRY_POINT,
  );
  const thinThreshold =
    resolvedOptions.thinSummaryWordThreshold ??
    DEFAULT_THIN_SUMMARY_WORD_THRESHOLD;

  const compilerOptions = resolveCompilerOptions(
    packageRoot,
    resolvedOptions.tsconfigPath,
  );

  const program = ts.createProgram({
    rootNames: [entryPoint],
    options: compilerOptions,
  });
  const checker = program.getTypeChecker();

  const entrySource = program.getSourceFile(entryPoint);
  if (!entrySource) {
    throw new Error(
      `analyzeTsDocCoverage: entry point not found in program: ${entryPoint}`,
    );
  }

  const moduleSymbol = checker.getSymbolAtLocation(entrySource);
  if (!moduleSymbol) {
    // No module symbol means the entry file declared no exports.
    return [];
  }

  const tsdocParser = new TSDocParser();
  const records: Array<TsDocCoverageRecord> = [];

  for (const exportSymbol of checker.getExportsOfModule(moduleSymbol)) {
    const resolved = resolveAlias(exportSymbol, checker);
    const declaration = pickPrimaryDeclaration(resolved);
    if (!declaration) {
      continue;
    }

    const kind = classifyDeclaration(declaration);
    const location = resolveLocation(declaration);
    const tsdoc = parseTsDocFor(declaration, tsdocParser);
    const summaryWordCount = tsdoc ? countSummaryWords(tsdoc) : 0;

    const hasSummary = summaryWordCount > 0;
    const hasThinSummary = hasSummary && summaryWordCount <= thinThreshold;
    const hasParams = tsdoc ? hasBlockTag(tsdoc, "@param") : false;
    const hasReturns = tsdoc
      ? hasBlockTag(tsdoc, "@returns") || hasBlockTag(tsdoc, "@return")
      : false;

    records.push({
      symbol: exportSymbol.getName(),
      kind,
      location,
      hasSummary,
      hasThinSummary,
      hasParams,
      hasReturns,
    });
  }

  // Sort by file then line so the report is deterministic across runs.
  records.sort((a, b) => {
    if (a.location.file !== b.location.file) {
      return a.location.file.localeCompare(b.location.file);
    }
    if (a.location.line !== b.location.line) {
      return a.location.line - b.location.line;
    }
    return a.symbol.localeCompare(b.symbol);
  });

  return records;
}

function resolveCompilerOptions(
  packageRoot: string,
  tsconfigPath: string | undefined,
): ts.CompilerOptions {
  if (tsconfigPath) {
    const absoluteTsconfig = path.resolve(packageRoot, tsconfigPath);
    const configFile = ts.readConfigFile(absoluteTsconfig, ts.sys.readFile);
    if (configFile.error) {
      throw new Error(
        `analyzeTsDocCoverage: failed to read tsconfig at ${absoluteTsconfig}: ${ts.flattenDiagnosticMessageText(configFile.error.messageText, "\n")}`,
      );
    }
    const parsed = ts.parseJsonConfigFileContent(
      configFile.config,
      ts.sys,
      path.dirname(absoluteTsconfig),
    );
    return { ...parsed.options, noEmit: true };
  }

  return {
    target: ts.ScriptTarget.ESNext,
    module: ts.ModuleKind.NodeNext,
    moduleResolution: ts.ModuleResolutionKind.NodeNext,
    allowJs: false,
    declaration: false,
    noEmit: true,
    skipLibCheck: true,
    strict: false,
    esModuleInterop: true,
    resolveJsonModule: true,
  };
}

function resolveAlias(symbol: ts.Symbol, checker: ts.TypeChecker): ts.Symbol {
  // `ts.SymbolFlags.Alias` covers `export { X } from`, `export * as ns`,
  // and `import` re-export chains. Walk the alias chain to the underlying
  // declaration so we read TSDoc from the original symbol, not the alias.
  let current = symbol;
  // Bound the loop to avoid pathological cycles in malformed graphs.
  for (let i = 0; i < 32; i++) {
    if (!isAlias(current)) {
      return current;
    }
    const next = checker.getAliasedSymbol(current);
    if (!next || next === current) {
      return current;
    }
    current = next;
  }
  return current;
}

function isAlias(symbol: ts.Symbol): boolean {
  // ts.SymbolFlags is a bit-field. The lint config bans bitwise
  // operators, so encapsulate the single bit-test in a tiny helper
  // and disable the rule for this one expression.
  // eslint-disable-next-line no-bitwise
  return (symbol.flags & ts.SymbolFlags.Alias) !== 0;
}

function pickPrimaryDeclaration(symbol: ts.Symbol): ts.Declaration | undefined {
  const declarations = symbol.getDeclarations();
  if (!declarations || declarations.length === 0) {
    return undefined;
  }
  // Prefer a declaration that is not just a re-export specifier — those
  // carry no TSDoc themselves; the underlying class/function does.
  const concrete = declarations.find(
    (d) =>
      !ts.isExportSpecifier(d) &&
      !ts.isExportAssignment(d) &&
      !ts.isExportDeclaration(d),
  );
  return concrete ?? declarations[0];
}

function classifyDeclaration(declaration: ts.Declaration): TsDocCoverageKind {
  if (ts.isClassDeclaration(declaration)) {
    return TsDocCoverageKind.Class;
  }
  if (ts.isInterfaceDeclaration(declaration)) {
    return TsDocCoverageKind.Interface;
  }
  if (ts.isTypeAliasDeclaration(declaration)) {
    return TsDocCoverageKind.TypeAlias;
  }
  if (ts.isEnumDeclaration(declaration)) {
    return TsDocCoverageKind.Enum;
  }
  if (
    ts.isFunctionDeclaration(declaration) ||
    ts.isMethodDeclaration(declaration)
  ) {
    return TsDocCoverageKind.Function;
  }
  if (
    ts.isVariableDeclaration(declaration) ||
    ts.isVariableStatement(declaration)
  ) {
    return TsDocCoverageKind.Variable;
  }
  if (ts.isModuleDeclaration(declaration)) {
    return TsDocCoverageKind.Module;
  }
  if (ts.isExportAssignment(declaration)) {
    return TsDocCoverageKind.Default;
  }
  return TsDocCoverageKind.Other;
}

function resolveLocation(declaration: ts.Declaration): {
  readonly file: string;
  readonly line: number;
} {
  // For variable declarations we want the line of the enclosing
  // `VariableStatement` (which is where the leading TSDoc lives).
  const target: ts.Node = ts.isVariableDeclaration(declaration)
    ? (declaration.parent.parent ?? declaration)
    : declaration;
  const source = target.getSourceFile();
  const { line } = source.getLineAndCharacterOfPosition(target.getStart());
  return { file: source.fileName, line: line + 1 };
}

function parseTsDocFor(
  declaration: ts.Declaration,
  parser: TSDocParser,
): DocComment | undefined {
  // For `VariableDeclaration` the leading TSDoc lives on the parent
  // `VariableStatement` — `getJSDocCommentRanges` walks back to the
  // statement-level comment block automatically when given the
  // statement node.
  const target: ts.Node = ts.isVariableDeclaration(declaration)
    ? (declaration.parent.parent ?? declaration)
    : declaration;

  const sourceText = target.getSourceFile().getFullText();
  const ranges = ts.getLeadingCommentRanges(sourceText, target.getFullStart());
  if (!ranges || ranges.length === 0) {
    return undefined;
  }

  // Pick the last block comment that opens with `/**` — that is the
  // declaration's TSDoc by convention.
  for (let i = ranges.length - 1; i >= 0; i--) {
    const range = ranges[i];
    if (range.kind !== ts.SyntaxKind.MultiLineCommentTrivia) {
      continue;
    }
    const text = sourceText.slice(range.pos, range.end);
    if (!text.startsWith("/**")) {
      continue;
    }
    const parsed = parser.parseString(text);
    return parsed.docComment;
  }
  return undefined;
}

function countSummaryWords(doc: DocComment): number {
  let words = 0;
  doc.summarySection.nodes.forEach((node) => {
    words += countWordsInNode(node);
  });
  return words;
}

function countWordsInNode(node: { kind: string } & object): number {
  // `DocPlainText` nodes carry the prose; everything else is
  // structural (paragraphs, soft breaks). Walk the tree shallowly.
  const anyNode = node as {
    kind: string;
    text?: string;
    getChildNodes?: () => ReadonlyArray<{ kind: string } & object>;
  };
  if (anyNode.kind === "PlainText" && typeof anyNode.text === "string") {
    return anyNode.text.split(/\s+/).filter((token) => token.trim().length > 0)
      .length;
  }
  let words = 0;
  const children = anyNode.getChildNodes?.() ?? [];
  for (const child of children) {
    words += countWordsInNode(child);
  }
  return words;
}

function hasBlockTag(doc: DocComment, tagName: string): boolean {
  if (tagName === "@param") {
    return doc.params.count > 0;
  }
  if (tagName === "@returns" || tagName === "@return") {
    return doc.returnsBlock !== undefined;
  }
  return doc.customBlocks.some((block) => block.blockTag.tagName === tagName);
}
